/**
 * Oferta partnera widziana przez klienta — strona publiczna.
 * =========================================================
 *
 * Wchodzi się tu z linku (`/oferta/:token`), bez logowania. Tożsamość niesie
 * token, dokładnie tak jak zakłada migracja 482 — dlatego strona nie prosi
 * o hasło i nie zakłada, że klient ma już konto w systemie.
 *
 * Od migracji 664 strona ma SPRZEDAWAĆ, nie tylko wyceniać. Właściciel,
 * czytając własną ofertę na telefonie (03.09.2026): „klient ma tylko nazwy
 * i ceny, nie wie co jest do czego, co mu to daje”. Stąd kolejność bloków:
 * korzyść → skład z rozwijanymi pozycjami (korzyści, funkcje, jak działa,
 * zrzuty, pytania) → kwota → decyzja. Tabela zniknęła: na telefonie ucinała
 * kolumnę z ceną i przewijała się w poziomie.
 *
 * „Jedno kliknięcie” traktujemy poważnie: przycisk akceptacji od razu uruchamia
 * pakiet, więc ekran mówi wprost, co się stanie, i pokazuje cały skład oferty
 * z kwotami PRZED kliknięciem. Nie ma tu drugiego kroku, ale nie ma też
 * niespodzianki. Z tego samego powodu na telefonie NIE ma przyklejonego
 * przycisku akceptacji — jest przyklejony pasek z kwotą i odsyłaczem do
 * decyzji; kliknięcie ma być świadome, nie odruchowe.
 *
 * Kwoty pochodzą wyłącznie z `oferta_publiczna_pokaz` — funkcja nie zwraca
 * prowizji partnera i przeglądarka nie ma jak jej policzyć.
 *
 * TRYB PODGLĄDU (897): ta sama strona jest podglądem partnera
 * (`/partner/oferty/:id/podglad`) — także dla szkicu, który tokenu jeszcze nie
 * ma. Dane jadą wtedy z `partner_oferta_podglad`, która oddaje TEN SAM jsonb
 * co `oferta_publiczna_pokaz` (wspólny rdzeń w bazie), a ekran chowa decyzję
 * klienta i mówi wprost, że to podgląd. Jedno źródło wyglądu — drugiej
 * reprezentacji oferty nie ma i nie ma jej być.
 *
 * DRUK (897): „Drukuj / Zapisz PDF” woła `window.print()`. Arkusz `@media
 * print` (`styles/index.css`, klasy `oferta-druk` / `druk-ukryj`) drukuje na
 * białym tle bez paska, przycisków i formularzy decyzji. Pozycje rozwijają się
 * przed drukiem wszystkie (`beforeprint`), bo zwinięty `Rozwijak` nie renderuje
 * treści — samym CSS nie da się jej pokazać.
 */

import { LogoInsolitum, SKALA_ZNAKU } from '@insolitum/ui';
import {
  AlertTriangle,
  ArrowDown,
  ArrowLeft,
  Check,
  Clock,
  Eye,
  FileText,
  LayoutGrid,
  Loader2,
  PartyPopper,
  Printer,
  QrCode,
  Sparkles,
  X,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { flushSync } from 'react-dom';
import { Link, useParams } from 'react-router-dom';

import { PozycjaOferty } from '../components/oferta/PozycjaOferty';
import {
  ETYKIETY_TRYBU,
  formatujDate,
  formatujKwote,
  indeksOtwartyNaStart,
  korzysciWSkrocie,
  type PozycjaOfertyDto,
  type TrybPozycji,
} from '../lib/oferta/karta';
import { supabase } from '../lib/supabase';

interface Oferta {
  numer: string;
  tytul: string | null;
  notatka: string | null;
  klient: string | null;
  partner: string | null;
  plan: string;
  tryb_rozliczenia: TrybPozycji;
  waluta: string;
  kwota_cykliczna: number;
  kwota_jednorazowa: number;
  kwota_netto: number;
  rabat_procent: number;
  kwota_rabatu: number;
  kwota_po_rabacie: number;
  status: string;
  wazna_do: string | null;
  zaakceptowana_at: string | null;
  mozna_akceptowac: boolean;
  /**
   * Promocja oferty (884): JEDNO stałe zdanie z katalogu promocji i termin
   * przyjmowania zamówień. Klucza brak przed 884 albo bez promocji — `null`.
   */
  promocja?: {
    klucz: string;
    nazwa: string | null;
    zdanie: string | null;
    zamowienia_do: string | null;
  } | null;
  pozycje: PozycjaOfertyDto[];
}

const ETYKIETY_STATUSU: Record<string, string> = {
  // Szkic widzi wyłącznie partner w podglądzie — klient dostaje ofertę wysłaną.
  szkic: 'Szkic — jeszcze niewysłana',
  wyslana: 'Czeka na Twoją decyzję',
  zaakceptowana: 'Zaakceptowana',
  odrzucona: 'Odrzucona',
  wycofana: 'Wycofana przez partnera',
  wygasla: 'Termin minął',
};

const KLASY_STATUSU: Record<string, string> = {
  wyslana: 'pill pill--pending',
  zaakceptowana: 'pill pill--active',
  odrzucona: 'pill pill--muted',
  wycofana: 'pill pill--muted',
  wygasla: 'pill pill--muted',
};

/** Podgląd partnera (897): oferta po identyfikatorze zamiast tokenu, bez decyzji klienta. */
export interface PodgladOferty {
  ofertaId: string;
  /** `partners.id` — tylko w trybie podglądu super-admina, inaczej `null`. */
  partnerId: string | null;
  /** Dokąd prowadzi „Wróć do ofert”. */
  powrot: string;
  /** „Drukuj / Zapisz PDF” z listy ofert: okno druku zaraz po wczytaniu. */
  drukujPoWczytaniu?: boolean;
}

interface Props {
  podglad?: PodgladOferty;
}

export function OfertaPublicznaPage({ podglad }: Props = {}) {
  const { token } = useParams<{ token: string }>();
  const trybPodgladu = Boolean(podglad);

  const [oferta, setOferta] = useState<Oferta | null>(null);
  const [wczytywanie, setWczytywanie] = useState(true);
  const [blad, setBlad] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState<'akceptacja' | 'odrzucenie' | null>(null);
  const [gotowe, setGotowe] = useState<string | null>(null);
  const [odrzucanie, setOdrzucanie] = useState(false);
  const [powod, setPowod] = useState('');
  const [otwarte, setOtwarte] = useState<Set<number>>(new Set());
  const [pasek, setPasek] = useState(false);

  const naglowekRef = useRef<HTMLDivElement>(null);
  const decyzjaRef = useRef<HTMLDivElement>(null);
  const komunikatRef = useRef<HTMLDivElement>(null);

  const ofertaIdPodgladu = podglad?.ofertaId ?? null;
  const partnerIdPodgladu = podglad?.partnerId ?? null;

  const wczytaj = useCallback(async () => {
    if (!ofertaIdPodgladu && !token) {
      setBlad('Link jest niepełny — brakuje w nim identyfikatora oferty.');
      setWczytywanie(false);
      return;
    }

    // Podgląd czyta ofertę po identyfikatorze (897) — ten sam wynik, bez zapisu
    // czegokolwiek w bazie; klient czyta po tokenie, jak od 482.
    const { data: wynik, error } = ofertaIdPodgladu
      ? await supabase.rpc('partner_oferta_podglad', {
          p_oferta_id: ofertaIdPodgladu,
          p_partner_id: partnerIdPodgladu,
        })
      : await supabase.rpc('oferta_publiczna_pokaz', {
          p_token: token,
        });

    if (error) {
      setBlad(error.message);
    } else {
      setBlad(null);
      const o = wynik as unknown as Oferta;
      setOferta(o);
      setOtwarte((stan) => {
        if (stan.size > 0) return stan;
        const i = indeksOtwartyNaStart(o.pozycje ?? []);
        return i === null ? stan : new Set([i]);
      });
    }
    setWczytywanie(false);
  }, [token, ofertaIdPodgladu, partnerIdPodgladu]);

  useEffect(() => {
    void wczytaj();
  }, [wczytaj]);

  // ── Druk ────────────────────────────────────────────────────────────────
  // Zwinięta pozycja nie ma treści w DOM-ie, więc przed drukiem rozwijamy
  // wszystkie (także przy Ctrl+P — `beforeprint`), a po druku wracamy do
  // stanu sprzed. `flushSync`, bo przeglądarka składa wydruk zaraz po
  // zdarzeniu i nie czeka na kolejny render.
  const przedDrukiem = useRef<Set<number> | null>(null);
  const liczbaPozycji = oferta?.pozycje?.length ?? 0;

  const rozwinDoDruku = useCallback(() => {
    if (przedDrukiem.current) return;
    flushSync(() =>
      setOtwarte((stan) => {
        przedDrukiem.current = stan;
        return new Set(Array.from({ length: liczbaPozycji }, (_, i) => i));
      })
    );
  }, [liczbaPozycji]);

  const zwinPoDruku = useCallback(() => {
    const stan = przedDrukiem.current;
    przedDrukiem.current = null;
    if (stan) setOtwarte(stan);
  }, []);

  useEffect(() => {
    window.addEventListener('beforeprint', rozwinDoDruku);
    window.addEventListener('afterprint', zwinPoDruku);
    return () => {
      window.removeEventListener('beforeprint', rozwinDoDruku);
      window.removeEventListener('afterprint', zwinPoDruku);
    };
  }, [rozwinDoDruku, zwinPoDruku]);

  const drukuj = useCallback(() => {
    rozwinDoDruku();
    window.print();
  }, [rozwinDoDruku]);

  // „Drukuj / Zapisz PDF” z listy ofert: okno druku raz, po wczytaniu oferty.
  const wydrukowano = useRef(false);
  const drukujPoWczytaniu = Boolean(podglad?.drukujPoWczytaniu);
  useEffect(() => {
    if (!drukujPoWczytaniu || !oferta || wydrukowano.current) return;
    wydrukowano.current = true;
    // Chwila na zrzuty ekranu w kartach — inaczej wydruk łapie puste ramki.
    const t = window.setTimeout(drukuj, 400);
    return () => window.clearTimeout(t);
  }, [drukujPoWczytaniu, oferta, drukuj]);

  // Pasek z kwotą pojawia się, gdy nagłówek oferty wyjedzie z ekranu.
  useEffect(() => {
    const el = naglowekRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const obs = new IntersectionObserver(([wpis]) => setPasek(!wpis.isIntersecting), {
      rootMargin: '-1px 0px 0px 0px',
    });
    obs.observe(el);
    return () => obs.disconnect();
  }, [oferta]);

  useEffect(() => {
    if (gotowe) komunikatRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [gotowe]);

  const przelacz = (i: number) =>
    setOtwarte((stan) => {
      const kopia = new Set(stan);
      if (kopia.has(i)) kopia.delete(i);
      else kopia.add(i);
      return kopia;
    });

  const zaakceptuj = async () => {
    setPracuje('akceptacja');
    setBlad(null);

    const start = Date.now();
    const { data: wynik, error } = await supabase.rpc('oferta_zaakceptuj', { p_token: token });
    // Minimalny czas widoczności stanu „uruchamiamy…”, żeby nie mignął.
    const reszta = 400 - (Date.now() - start);
    if (reszta > 0) await new Promise((r) => setTimeout(r, reszta));
    setPracuje(null);

    if (error) {
      // Odmowa licencji agenta (migracja 754: zmniejszenie, licencja
      // operatora, wykup) niesie kod w `message`, a zdanie dla człowieka
      // w `hint` — klient ma zobaczyć zdanie.
      setBlad(error.hint || error.message);
      return;
    }

    const w = wynik as unknown as { wynik?: string } | null;
    setGotowe(
      w?.wynik === 'juz_zaakceptowana'
        ? 'Ta oferta była już zaakceptowana — pakiet działa.'
        : 'Oferta zaakceptowana. Pakiet został uruchomiony.'
    );
    await wczytaj();
  };

  const odrzuc = async () => {
    setPracuje('odrzucenie');
    setBlad(null);

    const { error } = await supabase.rpc('oferta_odrzuc', {
      p_token: token,
      p_powod: powod.trim() || null,
    });
    setPracuje(null);

    if (error) {
      setBlad(error.message);
      return;
    }

    setOdrzucanie(false);
    setGotowe('Oferta odrzucona. Partner dostał informację.');
    await wczytaj();
  };

  const doDecyzji = () =>
    decyzjaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const partner = oferta?.partner?.trim() || 'partner Insolitum';
  // Decyzja należy do klienta — w podglądzie partnera jej nie ma (897).
  const decyzja = Boolean(oferta?.mozna_akceptowac) && !trybPodgladu;
  // Karta „co się stanie po akceptacji” — w podglądzie także dla szkicu, bo
  // tak zobaczy ją klient po wysłaniu; przyciski zastępuje zdanie.
  const kartaDecyzji =
    decyzja || (trybPodgladu && (oferta?.status === 'szkic' || oferta?.status === 'wyslana'));
  const skrot = oferta ? korzysciWSkrocie(oferta.pozycje ?? []) : [];
  const trybEtykieta = oferta
    ? (ETYKIETY_TRYBU[oferta.tryb_rozliczenia] ?? oferta.tryb_rozliczenia)
    : '';

  return (
    <div className="oferta-druk min-h-screen bg-aurora-bg text-aurora-text">
      {podglad && (
        <div
          className="druk-ukryj bg-aurora-brand/10 border-b border-aurora-brand/25"
          role="note"
          aria-label="Podgląd oferty"
        >
          <div className="max-w-6xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-aurora-text flex items-center gap-2 min-w-0">
              <Eye className="w-4 h-4 shrink-0 text-aurora-brand" aria-hidden="true" />
              <span>
                <strong>Podgląd — tak zobaczy ofertę klient.</strong>{' '}
                <span className="text-aurora-muted">
                  Przyciski decyzji klienta są tu wyłączone
                  {oferta?.status === 'szkic' ? '; szkic klient zobaczy dopiero po wysłaniu.' : '.'}
                </span>
              </span>
            </p>
            <div className="flex items-center gap-2 shrink-0">
              <Link to={podglad.powrot} className="btn btn--secondary btn--sm">
                <ArrowLeft className="w-4 h-4" aria-hidden="true" /> Wróć do ofert
              </Link>
              {oferta && <PrzyciskDruku onDrukuj={drukuj} />}
            </div>
          </div>
        </div>
      )}

      {oferta && pasek && (
        <div
          className="druk-ukryj sticky top-0 z-[100] bg-aurora-surface/90 backdrop-blur border-b border-aurora-border"
          role="status"
          aria-live="polite"
        >
          <div className="max-w-6xl mx-auto px-4 h-11 flex items-center justify-between gap-3 text-sm">
            <span className="truncate">
              <span className="text-aurora-muted">Razem netto · </span>
              <span className="mono tabular-nums font-semibold">
                {formatujKwote(oferta.kwota_po_rabacie, oferta.waluta)}
              </span>
              <span className="text-aurora-muted"> {trybEtykieta}</span>
            </span>
            {decyzja && (
              <button type="button" onClick={doDecyzji} className="btn btn--ghost btn--sm shrink-0">
                Do decyzji <ArrowDown className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-10">
        {/* Logo poziome zamiast sygnetu z dopisanym „INSOLITUM Universe" —
            napis krojem interfejsu obok znaku byłby fałszywym logo. */}
        <div className="mb-8 text-aurora-text">
          <LogoInsolitum uklad="poziome" wysokosc={SKALA_ZNAKU.naglowekTresci} />
        </div>

        {wczytywanie ? (
          <SzkieletOferty />
        ) : !oferta ? (
          <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl p-8 text-center max-w-3xl mx-auto">
            <AlertTriangle className="w-10 h-10 text-aurora-warning mx-auto mb-4" />
            <h1 className="display text-xl font-bold text-aurora-text mb-2">
              Nie możemy pokazać tej oferty
            </h1>
            <p className="text-sm text-aurora-muted">
              {blad ?? 'Link jest nieprawidłowy albo stracił ważność.'}
            </p>
            <p className="text-xs text-aurora-text-dim mt-4">
              Jeśli dostałeś ten link od partnera Insolitum, poproś go o nowy — oferta mogła zostać
              wycofana albo minął jej termin.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6 items-start">
            <div className="min-w-0">
              {gotowe && (
                <div
                  ref={komunikatRef}
                  className="druk-ukryj bg-aurora-success/10 border border-aurora-success/25 rounded-ids-md p-4 mb-6 flex items-start gap-3"
                  role="status"
                >
                  <PartyPopper className="w-5 h-5 text-aurora-success shrink-0 mt-0.5" />
                  <div className="text-sm text-aurora-success">
                    <p className="font-semibold">{gotowe}</p>
                    {oferta.status === 'zaakceptowana' && (
                      <p className="mt-1 text-aurora-text">
                        Zaloguj się do panelu, żeby zobaczyć, co już działa, i odebrać kody do
                        podłączenia urządzeń.{' '}
                        <Link to="/login" className="underline">
                          Przejdź do panelu
                        </Link>
                      </p>
                    )}
                  </div>
                </div>
              )}

              {blad && (
                <div
                  className="druk-ukryj bg-aurora-danger/10 border border-aurora-danger/20 rounded-ids-md p-4 mb-6 flex items-start gap-3"
                  role="alert"
                >
                  <AlertTriangle className="w-5 h-5 text-aurora-danger shrink-0 mt-0.5" />
                  <p className="text-sm text-aurora-danger">{blad}</p>
                </div>
              )}

              <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-3 overflow-hidden">
                {/* Nagłówek oferty */}
                <div ref={naglowekRef} className="p-5 sm:p-6 border-b border-aurora-border">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-aurora-text-dim mono mb-1">{oferta.numer}</p>
                      <h1 className="display text-2xl font-bold text-aurora-text leading-tight">
                        {oferta.tytul ?? 'Oferta Insolitum Universe'}
                      </h1>
                      <p className="text-sm text-aurora-muted mt-2 line-clamp-2">
                        dla <strong className="text-aurora-text">{oferta.klient ?? '—'}</strong>
                        {oferta.partner && <> · od {oferta.partner}</>}
                      </p>
                    </div>
                    <span className={KLASY_STATUSU[oferta.status] ?? 'pill pill--muted'}>
                      <i className="pin" aria-hidden="true" />
                      {ETYKIETY_STATUSU[oferta.status] ?? oferta.status}
                    </span>
                  </div>

                  {oferta.status === 'wyslana' && oferta.wazna_do && (
                    <p className="text-xs text-aurora-text-dim mt-3 flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                      Oferta obowiązuje do {formatujDate(oferta.wazna_do)}
                    </p>
                  )}

                  {oferta.promocja?.zdanie && (
                    <div
                      className="mt-3 rounded-ids-md border border-aurora-border bg-aurora-bg-subtle px-3 py-2"
                      data-promocja={oferta.promocja.klucz}
                    >
                      <p className="text-sm text-aurora-text">{oferta.promocja.zdanie}</p>
                      {oferta.promocja.zamowienia_do && (
                        <p className="text-xs text-aurora-text-dim mt-1">
                          Zamówienia w promocji do {formatujDate(oferta.promocja.zamowienia_do)}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {oferta.notatka && (
                  <div className="px-5 sm:px-6 py-4 border-b border-aurora-border bg-aurora-bg-subtle">
                    <p className="text-sm text-aurora-muted whitespace-pre-line">
                      {oferta.notatka}
                    </p>
                  </div>
                )}

                {/* Co zyskujesz w skrócie */}
                {skrot.length > 0 && (
                  <section
                    className="px-5 sm:px-6 py-5 border-b border-aurora-border"
                    aria-labelledby="skrot"
                  >
                    <h2 id="skrot" className="overline mb-3 flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-aurora-brand" aria-hidden="true" />
                      Co zyskujesz w skrócie
                    </h2>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {skrot.map((k) => (
                        <li
                          key={k.tytul}
                          className="flex items-start gap-2.5 rounded-ids-md border border-aurora-border bg-aurora-bg-subtle p-3"
                        >
                          <Check
                            className="w-4 h-4 mt-0.5 shrink-0 text-aurora-success"
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-aurora-text leading-snug">
                              {k.tytul}
                            </p>
                            {k.opis && (
                              <p className="mt-0.5 text-[13px] text-aurora-muted leading-relaxed line-clamp-3">
                                {k.opis}
                              </p>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}

                {/* Skład oferty */}
                <section className="px-5 sm:px-6 py-5" aria-labelledby="sklad">
                  <div className="flex items-baseline justify-between gap-3 mb-2">
                    <h2 id="sklad" className="overline">
                      Co wchodzi w skład oferty
                    </h2>
                    <span className="text-xs text-aurora-subtle">
                      {oferta.pozycje.length}{' '}
                      {oferta.pozycje.length === 1
                        ? 'pozycja'
                        : oferta.pozycje.length < 5
                          ? 'pozycje'
                          : 'pozycji'}
                    </span>
                  </div>
                  <p className="text-xs text-aurora-subtle mb-3">
                    Rozwiń pozycję, żeby zobaczyć, co dostajesz i jak to działa w praktyce.
                  </p>
                  <div className="divide-y divide-aurora-border">
                    {oferta.pozycje.map((poz, i) => (
                      <PozycjaOferty
                        key={`${poz.typ}-${poz.nazwa}-${i}`}
                        id={`pozycja-${i}`}
                        pozycja={poz}
                        waluta={oferta.waluta}
                        otwarta={otwarte.has(i)}
                        onToggle={() => przelacz(i)}
                        partner={oferta.partner}
                      />
                    ))}
                  </div>
                </section>
              </div>

              <p className="text-xs text-aurora-text-dim mt-6 flex items-center justify-center gap-1.5 lg:hidden">
                <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                Kwoty są wartościami netto. Dokument nie jest fakturą — fakturę VAT wystawia
                Insolitum.
              </p>
            </div>

            {/* Kolumna decyzji — na desktopie przyklejona */}
            <aside className="lg:sticky lg:top-6 space-y-4" aria-label="Podsumowanie i decyzja">
              <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-2 p-5 space-y-2">
                <h2 className="overline mb-2">Podsumowanie</h2>
                <div className="flex justify-between text-sm gap-3">
                  <span className="text-aurora-muted">Abonament {trybEtykieta}</span>
                  <span className="text-aurora-text mono tabular-nums whitespace-nowrap">
                    {formatujKwote(oferta.kwota_cykliczna, oferta.waluta)}
                  </span>
                </div>
                {Number(oferta.kwota_jednorazowa) > 0 && (
                  <div className="flex justify-between text-sm gap-3">
                    <span className="text-aurora-muted">Opłaty jednorazowe</span>
                    <span className="text-aurora-text mono tabular-nums whitespace-nowrap">
                      {formatujKwote(oferta.kwota_jednorazowa, oferta.waluta)}
                    </span>
                  </div>
                )}
                {Number(oferta.rabat_procent) > 0 && (
                  <div className="flex justify-between text-sm gap-3">
                    <span className="text-aurora-muted">Rabat {Number(oferta.rabat_procent)}%</span>
                    <span className="text-aurora-success mono tabular-nums whitespace-nowrap">
                      −{formatujKwote(oferta.kwota_rabatu, oferta.waluta)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-baseline gap-3 pt-2 border-t border-aurora-border">
                  <span className="text-sm font-semibold text-aurora-text">Razem netto</span>
                  <span className="display text-lg font-bold text-aurora-text mono tabular-nums whitespace-nowrap">
                    {formatujKwote(oferta.kwota_po_rabacie, oferta.waluta)}
                  </span>
                </div>
                <p className="text-[11px] text-aurora-subtle">Do kwot doliczamy VAT.</p>
                <div className="druk-ukryj pt-2">
                  <PrzyciskDruku onDrukuj={drukuj} pelnaSzerokosc />
                </div>
              </div>

              <div ref={decyzjaRef} className="scroll-mt-14">
                {kartaDecyzji && (
                  <div className="druk-ukryj bg-aurora-surface border border-aurora-border rounded-ids-xl shadow-elev-2 p-5">
                    <h2 className="overline mb-3">Co się stanie po akceptacji</h2>
                    <ol className="space-y-2.5 mb-5">
                      {[
                        { Ikona: Zap, tekst: 'Uruchamiamy od razu — bez podpisów i czekania.' },
                        {
                          Ikona: LayoutGrid,
                          tekst: 'Wszystko z tej oferty pojawia się na Twoim koncie.',
                        },
                        {
                          Ikona: QrCode,
                          tekst: 'Kody do podłączenia urządzeń czekają w panelu administratora.',
                        },
                      ].map(({ Ikona, tekst }) => (
                        <li
                          key={tekst}
                          className="flex items-start gap-2.5 text-[13px] text-aurora-muted"
                        >
                          <span className="w-6 h-6 shrink-0 rounded-full bg-aurora-brand/15 text-aurora-brand grid place-items-center">
                            <Ikona className="w-3.5 h-3.5" aria-hidden="true" />
                          </span>
                          <span className="leading-relaxed pt-0.5">{tekst}</span>
                        </li>
                      ))}
                    </ol>

                    {trybPodgladu ? (
                      <p
                        className="rounded-ids-md border border-dashed border-aurora-border bg-aurora-bg-subtle p-3 text-[13px] text-aurora-muted leading-relaxed"
                        data-podglad-decyzji
                      >
                        Tu klient zobaczy przyciski „Akceptuję i uruchamiam” i „Nie teraz”. W
                        podglądzie są wyłączone — nic, co tu klikniesz, nie zmienia oferty.
                      </p>
                    ) : odrzucanie ? (
                      <div className="field">
                        <label className="field__label" htmlFor="powod-odrzucenia">
                          Co zdecydowało? (opcjonalnie — pomoże partnerowi przygotować lepszą
                          propozycję)
                        </label>
                        <textarea
                          id="powod-odrzucenia"
                          value={powod}
                          onChange={(e) => setPowod(e.target.value)}
                          rows={3}
                          className="input"
                          placeholder="Np. wracamy do rozmowy w przyszłym kwartale."
                        />
                        <div className="flex gap-3 mt-3">
                          <button
                            type="button"
                            onClick={() => setOdrzucanie(false)}
                            className="btn btn--secondary flex-1"
                          >
                            Wróć
                          </button>
                          <button
                            type="button"
                            onClick={() => void odrzuc()}
                            disabled={pracuje !== null}
                            className="btn btn--danger flex-1"
                          >
                            {pracuje === 'odrzucenie' ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <X className="w-4 h-4" />
                            )}
                            Odrzuć ofertę
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        <button
                          type="button"
                          onClick={() => void zaakceptuj()}
                          disabled={pracuje !== null}
                          className="btn btn--primary btn--lg w-full"
                        >
                          {pracuje === 'akceptacja' ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" /> Uruchamiamy…
                            </>
                          ) : (
                            <>
                              <Check className="w-4 h-4" /> Akceptuję i uruchamiam
                            </>
                          )}
                        </button>
                        {pracuje !== 'akceptacja' && (
                          <button
                            type="button"
                            onClick={() => setOdrzucanie(true)}
                            disabled={pracuje !== null}
                            className="btn btn--secondary w-full"
                          >
                            Nie teraz
                          </button>
                        )}
                        <p className="text-[11px] text-aurora-subtle leading-relaxed">
                          Klikając, potwierdzasz skład i kwoty powyżej. Faktura przyjdzie od{' '}
                          {partner}.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {oferta.status === 'zaakceptowana' && !gotowe && (
                  <div className="bg-aurora-success/10 border border-aurora-success/25 rounded-ids-xl p-5 flex items-start gap-3">
                    <Check
                      className="w-5 h-5 text-aurora-success shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <div className="text-sm">
                      <p className="text-aurora-success font-semibold">
                        Oferta zaakceptowana {formatujDate(oferta.zaakceptowana_at)} — pakiet jest
                        aktywny.
                      </p>
                      <Link to="/login" className="btn btn--tonal btn--sm mt-3">
                        Przejdź do panelu
                      </Link>
                    </div>
                  </div>
                )}

                {oferta.status === 'wygasla' && (
                  <KartaStanu>
                    Ta oferta straciła ważność {formatujDate(oferta.wazna_do)}. Poproś {partner} o
                    nową — zwykle to jedno zdanie e-mailem.
                  </KartaStanu>
                )}
                {oferta.status === 'wycofana' && (
                  <KartaStanu>
                    Partner wycofał tę ofertę. Jeśli rozmowa trwa, wkrótce dostaniesz nową.
                  </KartaStanu>
                )}
                {oferta.status === 'odrzucona' && !gotowe && (
                  <KartaStanu>Dziękujemy za odpowiedź — {partner} ją otrzymał.</KartaStanu>
                )}
              </div>

              <p className="text-xs text-aurora-text-dim hidden lg:flex items-center justify-center gap-1.5">
                <FileText className="w-3.5 h-3.5" aria-hidden="true" />
                Kwoty są wartościami netto. Dokument nie jest fakturą — fakturę VAT wystawia
                Insolitum.
              </p>
            </aside>
          </div>
        )}
      </div>
    </div>
  );
}

function PrzyciskDruku({
  onDrukuj,
  pelnaSzerokosc = false,
}: {
  onDrukuj: () => void;
  pelnaSzerokosc?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onDrukuj}
      className={`btn btn--secondary btn--sm${pelnaSzerokosc ? ' w-full' : ''}`}
    >
      <Printer className="w-4 h-4" aria-hidden="true" /> Drukuj / Zapisz PDF
    </button>
  );
}

function KartaStanu({ children }: { children: ReactNode }) {
  return (
    <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl p-5">
      <p className="text-sm text-aurora-muted leading-relaxed">{children}</p>
    </div>
  );
}

/** Szkielet zamiast spinnera na środku — kształt strony widać od pierwszej chwili. */
function SzkieletOferty() {
  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6"
      aria-busy="true"
      aria-label="Wczytywanie oferty"
    >
      <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl p-6 space-y-6">
        <div className="space-y-2">
          <div className="skel h-3 w-24" />
          <div className="skel h-7 w-3/4" />
          <div className="skel h-4 w-1/2" />
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-start gap-3">
            <div className="skel w-9 h-9 rounded-ids-md" />
            <div className="flex-1 space-y-2">
              <div className="skel h-4 w-2/3" />
              <div className="skel h-3 w-1/3" />
            </div>
            <div className="skel h-4 w-20" />
          </div>
        ))}
      </div>
      <div className="bg-aurora-surface border border-aurora-border rounded-ids-xl p-5 space-y-3">
        <div className="skel h-3 w-1/2" />
        <div className="skel h-3 w-2/3" />
        <div className="skel h-6 w-1/2" />
        <div className="skel h-12 w-full rounded-ids-md" />
      </div>
    </div>
  );
}
